/**
 * Calendar link builders for the add-to-calendar page.
 *
 * Pure module: no DOM, no React, no network. The DOM-touching half (handing
 * the .ics to the browser) lives in `downloadIcs.ts` so this stays trivially
 * unit-testable.
 *
 * All emitted timestamps are UTC (`...Z`). `DTSTART;TZID=` without an
 * accompanying VTIMEZONE component is not valid RFC 5545 and is handled
 * inconsistently across clients; a UTC instant is unambiguous everywhere.
 */

/**
 * How long a reservation blocks the guest's calendar.
 *
 * The `get-add-to-calendar-data` endpoint carries no duration, and no
 * restaurant field in this app exposes a turn length. The legacy Angular page
 * emitted `start === end` (a zero-length event), which is a bug, not a
 * contract worth preserving.
 */
export const RESERVATION_EVENT_DURATION_MINUTES = 120;

export interface CalendarEvent {
  title: string;
  description: string;
  location: string;
  organizerName: string;
  organizerEmail: string;
  start: Date;
  end: Date;
}

/**
 * `09/22/2026` → `2026-09-22`.
 *
 * The add-to-calendar endpoint emits `->format('m/d/Y')`, so the leading field
 * is always the month.
 *
 * Throws on a malformed or impossible date (e.g. month 13, 30 February).
 */
export function usDateToIso(mdY: string): string {
  const parts = mdY.split('/');
  const [month, day, year] = parts.map(Number);
  const valid =
    parts.length === 3 &&
    /^\d{1,2}$/.test(parts[0]) && /^\d{1,2}$/.test(parts[1]) && /^\d{4}$/.test(parts[2]) &&
    month >= 1 && month <= 12 &&
    day >= 1 && day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (!valid) throw new Error(`Unreadable date: ${mdY}`);
  return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
}

/**
 * `8:30 PM` → `20:30`.
 *
 * The endpoint emits `->format('g:i A')`. Noon and midnight are the cases that
 * trip naive conversions: 12 AM is hour 0, 12 PM is hour 12.
 */
export function amPmToHm(time: string): string {
  const [clock, meridiem] = time.trim().split(/\s+/);
  const [rawHour, minute] = clock.split(':');
  let hour = Number(rawHour) % 12;
  if (meridiem?.toUpperCase() === 'PM') hour += 12;
  return `${String(hour).padStart(2, '0')}:${minute}`;
}

/**
 * Resolve a wall-clock time in an IANA zone to the UTC instant it names.
 *
 * `Intl.DateTimeFormat` can render an instant *in* a zone but offers no
 * inverse, so we probe: format a guess back into the zone, measure how far the
 * result drifts from the wall clock we wanted, and correct by that much. One
 * correction lands every case where the guess and the answer share a UTC
 * offset; the second settles the DST-transition cases where shifting the guess
 * moves it across the boundary into a different offset.
 *
 * @param ymd  Calendar date as `YYYY-MM-DD`.
 * @param hm   Time of day as `HH:MM` (24h).
 * @param timeZone IANA zone name, e.g. `America/Santiago`.
 */
export function zonedWallClockToUtc(ymd: string, hm: string, timeZone: string): Date {
  // Without this, an absent zone reaches `Intl.DateTimeFormat` as `undefined`,
  // which resolves to the VIEWER's zone and silently returns an instant two or
  // three hours off — the reservation lands in the calendar at the wrong time
  // with nothing to notice. An invalid name at least throws on its own.
  if (!timeZone) {
    throw new RangeError('zonedWallClockToUtc requires an IANA time zone');
  }

  const [year, month, day] = ymd.split('-').map(Number);
  const [hour, minute] = hm.split(':').map(Number);
  const target = Date.UTC(year, month - 1, day, hour, minute, 0);

  let guess = target;
  for (let i = 0; i < 2; i++) {
    guess = target - zoneOffsetMs(new Date(guess), timeZone);
  }
  return new Date(guess);
}

/**
 * How far ahead of UTC `timeZone` runs at `instant`, in milliseconds.
 * Positive east of Greenwich (Europe/Madrid in summer → +7200000).
 */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)!.value);

  const asIfUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour'),
    read('minute'),
    read('second'),
  );

  // Both sides are whole seconds; the instant's own milliseconds would
  // otherwise leak into the offset.
  return asIfUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** `2026-09-22T23:30:00Z` → `20260922T233000Z`. */
function toIcsUtc(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/** `2026-09-22T23:30:00Z` → `20260922T233000Z` for Google's `dates` param. */
const toGoogleStamp = toIcsUtc;

/**
 * Escape a value for an iCalendar TEXT property (RFC 5545 §3.3.11).
 * Backslash first, or it would double-escape the escapes we add after it.
 */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\n|\r/g, '\\n');
}

/**
 * Escape a value for an iCalendar PARAMETER, e.g. `ORGANIZER;CN=`.
 *
 * Parameters are §3.2, not §3.3.11: `param-value = paramtext / quoted-string`,
 * `paramtext` excludes `"` `;` `:` `,`, and no backslash escape is defined for
 * them at all. Running a name like `Sushi Club, Palermo` through the TEXT
 * escaper emits `CN=Sushi Club\, Palermo`, which a strict parser reads as a
 * literal backslash plus the start of a second parameter value — a malformed
 * ORGANIZER that some clients reject along with the whole VEVENT.
 *
 * Callers wrap the result in double quotes, so the only thing that has to go is
 * anything a quoted-string cannot hold: `"` itself and control characters.
 */
function escapeIcsParam(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/"/g, "'").replace(/[\x00-\x1F\x7F]/g, ' ');
}

/**
 * Fold a content line to 75 octets (RFC 5545 §3.1), continuing with a leading
 * space. The limit counts octets, not characters, so accented restaurant names
 * have to be measured after UTF-8 encoding — and a fold must never land inside
 * a multi-byte sequence.
 */
function foldIcsLine(line: string): string {
  const encoder = new TextEncoder();
  if (encoder.encode(line).length <= 75) return line;

  const out: string[] = [];
  let current = '';
  let currentBytes = 0;
  // The continuation space consumes one of the 75 octets on every line but
  // the first.
  let limit = 75;

  // Iterating the string yields whole code points, so a surrogate pair is
  // never split down the middle.
  for (const char of line) {
    const charBytes = encoder.encode(char).length;
    if (currentBytes + charBytes > limit) {
      out.push(current);
      current = '';
      currentBytes = 0;
      limit = 74;
    }
    current += char;
    currentBytes += charBytes;
  }
  out.push(current);

  return out.join('\r\n ');
}

const ORGANIZER_EMAIL = /^[^\s@;:,"]+@[^\s@;:,"]+$/;

/**
 * Build an RFC 5545 VCALENDAR carrying a single VEVENT.
 *
 * `uid` is injected rather than generated so tests stay deterministic; callers
 * pass the reservation's encrypted id, which also means re-importing the same
 * link updates the existing event instead of duplicating it.
 */
export function buildIcs(event: CalendarEvent, uid: string): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Meitre//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${escapeIcsText(uid)}@meitre.com`,
    `DTSTAMP:${toIcsUtc(new Date())}`,
    `DTSTART:${toIcsUtc(event.start)}`,
    `DTEND:${toIcsUtc(event.end)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    `DESCRIPTION:${escapeIcsText(event.description)}`,
    `LOCATION:${escapeIcsText(event.location)}`,
    // Calendar clients import the event without ORGANIZER, so an address that
    // could end the line or split the value is dropped rather than written raw.
    ...(ORGANIZER_EMAIL.test(event.organizerEmail)
      ? [`ORGANIZER;CN="${escapeIcsParam(event.organizerName)}":mailto:${event.organizerEmail}`]
      : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.map(foldIcsLine).join('\r\n');
}

export function buildGoogleCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${toGoogleStamp(event.start)}/${toGoogleStamp(event.end)}`,
    details: event.description,
    location: event.location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function buildOutlookUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title,
    startdt: event.start.toISOString(),
    enddt: event.end.toISOString(),
    body: event.description,
    location: event.location,
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}
